export const initHeader = (): void => {
  const header = document.querySelector<HTMLElement>('header') ?? document;
  const primaryHeader = document.querySelector<HTMLElement>('.primaryHeader');
  const mobileMenu = header.querySelector<HTMLElement>('.headerMenu');
  const hamburger = header.querySelector<HTMLElement>('.headerMainHamburger .c-btn');
  const closeMobileMenu = header.querySelector<HTMLElement>('#closeMobileMenu');
  const headerMainWWW = header.querySelector<HTMLElement>('.headerMain.--www');

  if (headerMainWWW) {
    const scrollOffset = 70;
    const scrollDownOffset = 900; 
    let isScrollState = false;
    let isScrollTicking = false;
    let lastScrollY = window.scrollY;
    let scrollDownStartY = window.scrollY;

    const updateHeaderScrollState = (): void => {
      const currentScrollY = Math.max(window.scrollY, 0);
      const shouldHaveScrollState = currentScrollY >= scrollOffset;
      const isScrollingUp = currentScrollY < lastScrollY;
      const isScrollingDown = currentScrollY > lastScrollY;

      if (shouldHaveScrollState !== isScrollState) {
        isScrollState = shouldHaveScrollState;
        headerMainWWW.classList.toggle('--scroll', shouldHaveScrollState);

        if (shouldHaveScrollState) {
          scrollDownStartY = isScrollingDown ? scrollOffset : currentScrollY;
        } 
      }

      if (!shouldHaveScrollState) {
        scrollDownStartY = currentScrollY;
        headerMainWWW.classList.remove('--scroll-down');
        lastScrollY = currentScrollY;
        return;
      }

      if (isScrollingUp) {
        scrollDownStartY = currentScrollY;
        headerMainWWW.classList.remove('--scroll-down');
      } else if (isScrollingDown && currentScrollY - scrollDownStartY >= scrollDownOffset) {
        headerMainWWW.classList.add('--scroll-down');
      }

      lastScrollY = currentScrollY;
    };

    const requestHeaderScrollUpdate = (): void => {
      if (isScrollTicking) return;

      isScrollTicking = true;
      window.requestAnimationFrame(() => {
        updateHeaderScrollState();
        isScrollTicking = false;
      });
    };

    updateHeaderScrollState();
    window.addEventListener('scroll', requestHeaderScrollUpdate, { passive: true });
  }

  if (mobileMenu) {
    const syncPageScrollLock = (): void => {
      const isMenuOpen = mobileMenu.classList.contains('--showMobile');

      document.body.classList.toggle(
        '--mobile-menu-open',
        isMenuOpen,
      );
 
      if (!isMenuOpen) {
        mobileMenu.querySelectorAll<HTMLElement>('.wrapper-subnav.--active').forEach((submenu) => {
          submenu.classList.remove('--active');
        });
        mobileMenu.querySelectorAll<HTMLElement>('.menu-item.has-submenu > button').forEach((trigger) => {
          trigger.setAttribute('aria-expanded', 'false');
        });
      }
    };

    syncPageScrollLock();
    new MutationObserver(syncPageScrollLock).observe(mobileMenu, {
      attributes: true,
      attributeFilter: ['class'],
    });
  }

  header.querySelectorAll<HTMLElement>('.headerMenu.--www .menu-item.has-submenu').forEach((item) => {
    const submenuTrigger = item.querySelector<HTMLButtonElement>(':scope > button');
    const submenuLink = submenuTrigger?.querySelector<HTMLAnchorElement>('a');
    const submenuWrapper = item.querySelector<HTMLElement>(':scope > .wrapper-subnav');
    const submenuBack = submenuWrapper?.querySelector<HTMLElement>('.submenu-back');
    const submenuName = submenuWrapper?.querySelector<HTMLElement>('.submenu-name');

    if (!submenuTrigger || !submenuWrapper) return;

    submenuTrigger.setAttribute('aria-expanded', 'false');

    submenuTrigger.addEventListener('click', (event: MouseEvent) => {
      const target = event.target as Element | null;
      if (target?.closest('a')) return;

      event.preventDefault();
      if (submenuLink && submenuName) {
        submenuName.textContent = submenuLink.textContent?.trim() ?? '';
      }
      submenuWrapper.classList.add('--active');
      submenuTrigger.setAttribute('aria-expanded', 'true');
    });

    submenuBack?.addEventListener('click', () => {
      submenuWrapper.classList.remove('--active');
      submenuTrigger.setAttribute('aria-expanded', 'false');
      submenuTrigger.focus();
    });
  });

  header.querySelectorAll<HTMLLIElement>('.headerMain.--www .menu-item.has-submenu.info-item').forEach((infoItem) => {
    const trigger = infoItem.querySelector<HTMLButtonElement>(':scope > .blog-accordeon > .header-submenu-toggle');
    const parentLink = infoItem.querySelector<HTMLAnchorElement>(':scope > .blog-accordeon > .header-parent-link');
    const wrapper = infoItem.querySelector<HTMLElement>(':scope > .wrapper-subnav');
    const submenu = wrapper?.querySelector<HTMLElement>('.subnav');
    const closeTransitionDelay = 240;
    let openAnimationFrame: number | undefined;
    let closeTransitionTimer: number | undefined;
 
    if (!trigger || !wrapper || !submenu) return;

    const setOpen = (isOpen: boolean, immediate = false): void => {
      if (openAnimationFrame) {
        window.cancelAnimationFrame(openAnimationFrame);
        openAnimationFrame = undefined;
      }
      if (closeTransitionTimer) {
        window.clearTimeout(closeTransitionTimer);
        closeTransitionTimer = undefined;
      }

      if (isOpen) {
        wrapper.classList.remove('d-none');
        openAnimationFrame = window.requestAnimationFrame(() => {
          openAnimationFrame = undefined;
          infoItem.classList.add('--active');
          submenu.classList.add('show');
          trigger.setAttribute('aria-expanded', 'true');
        });

        return;
      }

      infoItem.classList.remove('--active');
      submenu.classList.remove('show');
      trigger.setAttribute('aria-expanded', 'false');

      if (immediate) {
        wrapper.classList.add('d-none');
        return;
      }

      closeTransitionTimer = window.setTimeout(() => {
        wrapper.classList.add('d-none');
      }, closeTransitionDelay);
    };

    const isOpen = (): boolean => trigger.getAttribute('aria-expanded') === 'true';
    const closeOtherDesktopSubmenus = (): void => {
      header.querySelectorAll<HTMLButtonElement>('.headerMain.--www .menu-item.has-submenu.info-item .header-submenu-toggle[aria-expanded="true"]').forEach((openTrigger) => {
        if (openTrigger === trigger) return;

        openTrigger.click();
      });
    };
    const toggleByClick = (event: MouseEvent): void => {
      event.preventDefault();
      closeOtherDesktopSubmenus();
      setOpen(!isOpen());
    };

    setOpen(false, true);

    trigger.addEventListener('click', toggleByClick);
    parentLink?.addEventListener('click', toggleByClick);

    infoItem.addEventListener('focusout', (event: FocusEvent) => {
      const nextTarget = event.relatedTarget as Node | null;

      if (!nextTarget || !infoItem.contains(nextTarget)) {
        setOpen(false);
      }
    });

    document.addEventListener('click', (event: MouseEvent) => {
      const target = event.target as Node | null;
      if (!target || !isOpen() || infoItem.contains(target)) return;

      setOpen(false);
    });

    document.addEventListener('keydown', (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen()) {
        setOpen(false);
        trigger.focus();
      }
    });
  });
  const closeMobile = (): void => {
    mobileMenu?.classList.remove('--showMobile');
    mobileMenu?.setAttribute('aria-hidden', 'true');
    hamburger?.setAttribute('aria-expanded', 'false');
    primaryHeader?.classList.remove('--openMenu');
  };

  const desktopMenuQuery = window.matchMedia('(min-width: 1200px)');
  const closeMobileOnDesktop = (event: MediaQueryList | MediaQueryListEvent): void => {
    if (event.matches) {
      closeMobile();
    }
  };

  closeMobileOnDesktop(desktopMenuQuery);
  desktopMenuQuery.addEventListener('change', closeMobileOnDesktop);

  hamburger?.setAttribute('aria-controls', 'mobileMenu');
  hamburger?.setAttribute('aria-expanded', 'false');

  hamburger?.addEventListener('click', (event: MouseEvent) => {
    event.preventDefault();
    mobileMenu?.classList.add('--showMobile');
    mobileMenu?.setAttribute('aria-hidden', 'false');
    hamburger?.setAttribute('aria-expanded', 'true');
    primaryHeader?.classList.add('--openMenu');
  });

  closeMobileMenu?.addEventListener('click', () => {
    closeMobile();
  });

  document.addEventListener('keydown', (event: KeyboardEvent) => {
    if (event.key === 'Escape' && mobileMenu?.classList.contains('--showMobile')) {
      closeMobile();
      hamburger?.focus();
    }
  });
};
